/**
 * Capitalizes the first letter of a string.
 * @param str - The input string.
 * @returns The string with the first letter capitalized.
 */
export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Converts a string to kebab-case.
 * @param str - The input string.
 * @returns The string in kebab-case.
 */
export const toKebabCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
};

/**
 * Truncates a string to a specified length and adds an ellipsis if necessary.
 * @param str - The input string.
 * @param length - The maximum length of the string.
 * @returns The truncated string.
 */
export const truncate = (str: string, length: number): string => {
  if (str.length <= length) return str;
  return str.slice(0, length) + "...";
};

/**
 * Reverses the characters in a string.
 * @param str - The input string.
 * @returns The reversed string.
 */
export const reverseString = (str: string): string => {
  return str.split("").reverse().join("");
};

/**
 * Checks if a string is a palindrome (ignores case and spaces).
 * @param str - The input string.
 * @returns True if the string is a palindrome, otherwise false.
 */
export const isPalindrome = (str: string): boolean => {
  const sanitized = str.replace(/[\s]/g, "").toLowerCase();
  return sanitized === reverseString(sanitized);
};

/**
 * Counts the number of words in a string.
 * @param str - The input string.
 * @returns The word count.
 */
export const wordCount = (str: string): number => {
  return str.trim().split(/\s+/).length;
};

/**
 * Converts a string to title case.
 * @param str - The input string.
 * @returns The string in title case.
 */
export const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ");
};

/**
 * Truncates a string by keeping the first and last parts
 * and inserting "..." in the middle if it exceeds a certain length.
 * Example: truncateMiddleString("NewDataRow.Pdf", 2, 5) → "Ne...ow.Pdf".
 * @param str - The input string to truncate.
 * @param startChars - Number of characters to keep at the beginning of the string. Default is 2.
 * @param endChars - Number of characters to keep at the end of the string. Default is 5.
 * @returns The truncated string with ellipsis in the middle if applicable.
 */
export function truncateMiddleString(
  str: string,
  startChars = 4,
  endChars = 8
): string {
  if (!str) return "";
  const totalKeep = startChars + endChars;
  if (str.length <= totalKeep + 3) return str;
  return `${str.slice(0, startChars)}...${str.slice(-endChars)}`;
}
